import React from "react";
import { Box, Divider } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import RemoveIcon from "@mui/icons-material/Remove";
import HelpIcon from "@mui/icons-material/Help";
import BalanceTooltip from "src/components/wallets/header/BalanceTooltip";
import {
  CalculationsWrapper,
  BreakdownContainer,
  CollapseContent,
  CalcText,
} from "../styles/Trade.styles";

interface SummaryLineItem {
  label: string;
  value: string;
  icon?: "clear" | "remove";
  tooltip?: string;
}

interface TradeSummaryProps {
  title: string;
  amount: string;
  currency: string;
  lineItems: SummaryLineItem[];
  fee: number | string;
  feeTooltip?: string;
  expectedOutput?: {
    label: string;
    value: string;
  };
  showDivider?: boolean;
  children?: React.ReactNode; // For promotional placard or other custom content
}

const IconWrapper = ({ children }: { children: React.ReactNode }) => (
  <Box
    width="16px"
    height="16px"
    display="flex"
    justifyContent="center"
    alignItems="center"
    borderRadius="25px"
    bgcolor="#E5E9EB">
    {children}
  </Box>
);

export const TradeSummary: React.FC<TradeSummaryProps> = ({
  title,
  amount,
  currency,
  lineItems,
  fee,
  feeTooltip,
  expectedOutput,
  showDivider = true,
  children,
}) => {
  return (
    <CalculationsWrapper>
      <BreakdownContainer>
        <CollapseContent>
          <Box display="flex" justifyContent="space-between" width="100%">
            <CalcText>{title}</CalcText>
            <CalcText>
              {amount} {currency}
            </CalcText>
          </Box>
          {lineItems.map((item, index) => (
            <Box key={index} display="flex" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" gap="12px">
                {item.icon && (
                  <IconWrapper>
                    {item.icon === "clear" ? (
                      <ClearIcon sx={{ width: "8px", color: "#002035" }} />
                    ) : (
                      <RemoveIcon sx={{ width: "8px", color: "#002035" }} />
                    )}
                  </IconWrapper>
                )}
                <CalcText weight={400}>{item.label}</CalcText>
                {item.tooltip && (
                  <BalanceTooltip message={item.tooltip} contrast>
                    <HelpIcon sx={{ width: "12px", height: "12px", color: "#002035" }} />
                  </BalanceTooltip>
                )}
              </Box>
              <CalcText weight={400}>{item.value}</CalcText>
            </Box>
          ))}
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center" gap="12px">
              <IconWrapper>
                <RemoveIcon sx={{ width: "8px", color: "#002035" }} />
              </IconWrapper>
              <CalcText weight={400}>Broker Fee</CalcText>
              {feeTooltip && (
                <BalanceTooltip message={feeTooltip} contrast>
                  <HelpIcon sx={{ width: "12px", height: "12px", color: "#002035" }} />
                </BalanceTooltip>
              )}
            </Box>
            <CalcText weight={400}>{fee}%</CalcText>
          </Box>
          {(showDivider || expectedOutput) && (
            <>
              <Divider
                sx={{
                  background: "#E5E7E8",
                  width: "100%",
                  borderBottomWidth: "0",
                  height: "1px",
                }}
              />
              <Box display="flex" width="100%" gap="12px" flexDirection="column">
                {children}
                {expectedOutput && (
                  <Box display="flex" justifyContent="space-between">
                    <CalcText>{expectedOutput.label}</CalcText>
                    <CalcText>{expectedOutput.value}</CalcText>
                  </Box>
                )}
              </Box>
            </>
          )}
        </CollapseContent>
      </BreakdownContainer>
    </CalculationsWrapper>
  );
};
